/**
 * Fold repeated (kind, text) runs of the raw bot_events "activity" stream into
 * one row with a `count`, the way `decisions.ts` already collapses ladder
 * rungs into `rungs`. Pure, no `Db`/server-only imports — shared by the server
 * (`fullState()`'s batch load) and the client (`ws.ts`'s live reducer).
 *
 * Same motivation as `decisions.ts`'s multi-run fix: a throttled retry (e.g.
 * a take-profit reprice cycle) emits several DIFFERENT kinds per tick
 * (`rejected`, `intent`, `log`) that interleave in the stream, so a same-key
 * run must stay open across intervening other-key events, not just merge with
 * whatever immediately precedes it.
 */

/** Same window `decisions.ts` uses for a ladder-burst/retry-cycle run. */
export const EVENT_FOLD_GAP_MS = 120_000;

export type FoldableEvent = { ts: string; kind: string; text: string };
export type FoldedEvent = FoldableEvent & { count: number };

const keyOf = (e: FoldableEvent): string => `${e.kind}|${e.text}`;

/**
 * Batch fold a chronological (oldest-first) array of raw events. One open run
 * per key at a time; a run closes (flushes into the output) when a same-key
 * event arrives more than `EVENT_FOLD_GAP_MS` later, or at the end of the
 * batch — flushed in `lastTs` order so the result stays chronological.
 */
export function foldEvents(events: FoldableEvent[]): FoldedEvent[] {
  const out: FoldedEvent[] = [];
  const open = new Map<string, FoldedEvent & { lastTs: number }>();

  for (const e of events) {
    const tMs = Date.parse(e.ts);
    const key = keyOf(e);
    const run = open.get(key);
    if (run && tMs - run.lastTs <= EVENT_FOLD_GAP_MS) {
      run.count++;
      run.ts = e.ts;
      run.lastTs = tMs;
    } else {
      if (run) out.push(run);
      open.set(key, { ...e, count: 1, lastTs: tMs });
    }
  }
  for (const run of [...open.values()].sort((a, b) => a.lastTs - b.lastTs)) out.push(run);

  return out;
}

/**
 * Merge one live incoming event into an already-folded, chronological array:
 * if a same-key run is still open (its last occurrence within
 * `EVENT_FOLD_GAP_MS`), bump its count and move it to the end (it just
 * happened again); otherwise append a fresh single-count entry.
 */
export function mergeLiveEvent(existing: FoldedEvent[], e: FoldableEvent): FoldedEvent[] {
  const tMs = Date.parse(e.ts);
  const key = keyOf(e);
  for (let i = existing.length - 1; i >= 0; i--) {
    const row = existing[i]!;
    if (tMs - Date.parse(row.ts) > EVENT_FOLD_GAP_MS) break; // older rows are only further away
    if (keyOf(row) !== key) continue;
    const merged = { ...row, ts: e.ts, count: row.count + 1 };
    return [...existing.slice(0, i), ...existing.slice(i + 1), merged];
  }
  return [...existing, { ...e, count: 1 }];
}
